import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Field, Badge, Center, Spinner, PageHeader } from "@/components/ui";
import { Topbar } from "@/components/Layout";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [pwErr, setPwErr] = useState(null);
  const [delPw, setDelPw] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const p = await api.get("/profile"); setFullName(p.full_name || ""); setPhone(p.phone || ""); setEmail(p.email || ""); setRole(p.role || ""); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg(null); setErr(null);
    try { await api.patch("/profile", { full_name: fullName, phone }); await refresh(); setMsg("Profile saved."); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not save."); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 3000); }
  };
  const changePassword = async () => {
    setPwMsg(null); setPwErr(null);
    if (newPw.length < 8) { setPwErr("New password must be at least 8 characters."); return; }
    if (newPw !== confirmPw) { setPwErr("New passwords do not match."); return; }
    setPwBusy(true);
    try { await api.post("/auth/change-password", { current_password: curPw, new_password: newPw }); setCurPw(""); setNewPw(""); setConfirmPw(""); setPwMsg("Password updated."); }
    catch (e) { setPwErr(e instanceof ApiError ? e.message : "Could not change password."); }
    finally { setPwBusy(false); setTimeout(() => setPwMsg(null), 3000); }
  };
  const doDelete = async () => {
    setDelErr(null);
    if (!delPw) { setDelErr("Enter your password to confirm."); return; }
    if (!window.confirm("Permanently delete your account and personal data? This cannot be undone.")) return;
    setDelBusy(true);
    try { await api.post("/account/delete", { password: delPw }); await logout(); nav("/"); }
    catch (e) { setDelErr(e instanceof ApiError ? e.message : "Could not delete account."); setDelBusy(false); }
  };

  if (loading) return <div className="screen"><Topbar title="Profile" /><Center><Spinner /></Center></div>;

  return (
    <div className="screen" data-testid="profile-screen">
      <Topbar title="Profile" subtitle={role ? role.replace(/_/g, " ") : undefined} />
      <div className="container-narrow">
        <PageHeader title="Profile" onBack={() => nav(-1)} />
        <Card>
          <div className="tag">YOUR DETAILS</div>
          <Field testID="profile-name" label="Full name" value={fullName} onChange={setFullName} placeholder="Your name" />
          <Field testID="profile-phone" label="Phone" value={phone} onChange={setPhone} placeholder="(201) 555-0100" />
          <div className="kv"><span className="k">email</span><span className="v mono">{email}</span></div>
          {msg ? <Badge testID="profile-toast" label={msg} tone="success" /> : null}
          {err ? <p className="err-text" data-testid="profile-error">{err}</p> : null}
          <Button testID="profile-save" label="Save changes" onClick={save} loading={saving} />
        </Card>
        <Card>
          <div className="tag">CHANGE PASSWORD</div>
          <Field testID="cur-password" label="Current password" type="password" value={curPw} onChange={setCurPw} placeholder="••••••••" />
          <Field testID="new-password" label="New password (min 8)" type="password" value={newPw} onChange={setNewPw} placeholder="••••••••" />
          <Field testID="confirm-password" label="Confirm new password" type="password" value={confirmPw} onChange={setConfirmPw} placeholder="••••••••" />
          {pwMsg ? <Badge testID="pw-toast" label={pwMsg} tone="success" /> : null}
          {pwErr ? <p className="err-text" data-testid="pw-error">{pwErr}</p> : null}
          <Button testID="change-password" variant="secondary" label="Update password" onClick={changePassword} loading={pwBusy} />
        </Card>
        <Card>
          <div className="tag">LEGAL</div>
          <Button testID="profile-terms" variant="ghost" icon="file-text" label="Terms of Service" onClick={() => nav("/terms")} />
          <div style={{ height: 8 }} />
          <Button testID="profile-privacy" variant="ghost" icon="shield" label="Privacy Policy" onClick={() => nav("/privacy")} />
        </Card>
        {role !== "gm" ? (
          <Card>
            <div className="tag" style={{ color: "var(--error)" }}>DELETE ACCOUNT</div>
            <p className="note">Permanently delete your account and personal data. This cannot be undone. Enter your password to confirm.</p>
            <Field testID="delete-password" label="Password" type="password" value={delPw} onChange={setDelPw} placeholder="••••••••" />
            {delErr ? <p className="err-text" data-testid="delete-error">{delErr}</p> : null}
            <Button testID="delete-account" variant="danger" icon="trash-2" label="Delete my account" onClick={doDelete} loading={delBusy} />
          </Card>
        ) : null}
      </div>
    </div>
  );
}
